import * as React from 'react';
import { Alert } from 'reactstrap';
import { AccentButton } from '../controls/AccentButton';
import { AccentSpinner } from '../controls/AccentSpinner';
import { AccentCheckUnbound } from '../controls/unbound/AccentCheckUnbound';
import { advancedFilterCache } from '../controls/advancedfilter/AdvancedFilterConstants';
import { accentUtils, showDialog, t } from '../services/HelperService';
import { AccentDialog } from './AccentDialog';

/**
 * Confirmation shown by a grid's "Reset Layout" action.
 *
 * quickFilters is the grid's saved QuickFilters setting ([{ ID, Selected, Name? }]) and filterSchemaName the
 * entity type the buttons were chosen for. Names are taken from the items when every item carries one,
 * otherwise resolved from the (already cached) quick filter list.
 *
 * The remaining flags describe what the grid offers, so the summary only lists things that grid can actually
 * reset: hasColumnLayout (resizable / hideable columns, default true), advancedFilterMode ("saved" for the
 * Advanced Filter toolbar with selectable saved filters, "builder" for the inline Filter button panel, anything
 * else for none), selectedAdvancedFilterName (name of the currently selected saved Advanced Filter, if any),
 * hasUnsavedAdvancedFilter (ad-hoc Advanced Filter conditions are in effect), hasSearch (search box),
 * hasDetails (row details), paged (page navigation rather than infinite scroll).
 *
 * Resolves { canceled: true } or { canceled: false, removeQuickFilters }.
 */
export function showResetGridLayoutDlg({ quickFilters, filterSchemaName, hasColumnLayout, advancedFilterMode, selectedAdvancedFilterName, hasUnsavedAdvancedFilter, hasSearch, hasDetails, paged }) {
    return showDialog(<ResetGridLayoutDlg
        quickFilters={quickFilters ?? []}
        filterSchemaName={filterSchemaName}
        hasColumnLayout={hasColumnLayout !== false}
        advancedFilterMode={advancedFilterMode}
        selectedAdvancedFilterName={selectedAdvancedFilterName}
        hasUnsavedAdvancedFilter={hasUnsavedAdvancedFilter === true}
        hasSearch={hasSearch === true}
        hasDetails={hasDetails === true}
        paged={paged === true}
    />);
}

class ResetGridLayoutDlg extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            removeQuickFilters: false,
            filterNames: null
        };

        this.onOK = this.onOK.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onToggleRemove = this.onToggleRemove.bind(this);
    }

    get hasQuickFilters() {
        return (this.props.quickFilters?.length ?? 0) > 0;
    }

    async componentDidMount() {
        if (!this.hasQuickFilters) return;

        if (this.props.quickFilters.every(qf => !accentUtils.isEmpty(qf.Name))) {
            this.setState({ filterNames: this.props.quickFilters.map(qf => qf.Name) });
            return;
        }

        let definitions = [];
        try {
            definitions = (await advancedFilterCache.getQuickFilters(this.props.filterSchemaName)) ?? [];
        } catch {
            definitions = [];
        }

        // IDs may be stored as string or number (see RTLGridQuickFilterBar), so compare as strings.
        // A button whose definition is no longer visible to the user is listed by ID so nothing is hidden.
        const names = this.props.quickFilters.map(qf => {
            const def = definitions.find(d => `${d.ID}` === `${qf.ID}`);
            return accentUtils.isNull(def) ? `${qf.ID}` : def.Name;
        });

        this.setState({ filterNames: names });
    }

    onToggleRemove(e) {
        this.setState({ removeQuickFilters: e.checked });
    }

    onCancel() {
        this.props.onClose({ canceled: true });
    }

    onOK() {
        this.props.onClose({ canceled: false, removeQuickFilters: this.hasQuickFilters && this.state.removeQuickFilters });
    }

    render() {
        const { removeQuickFilters, filterNames } = this.state;

        return (
            <AccentDialog
                id="resetGridLayoutDlg"
                className="accentOKDlg"
                headerContent="Reset Grid Layout"
                onClose={this.onCancel}
                maxWidth={520}
                actionsContent={[
                    <AccentButton key="okBtn" tagName="resetGridLayoutOkBtn" onClick={this.onOK}>
                        {t("application_strings.application.buttons.ok")}
                    </AccentButton>
                ]}
            >
                <div>
                    <p className="okDlgBody mb-0">Resetting the layout will:</p>
                    <ul className="mb-2 mt-1">
                        {this.props.hasColumnLayout && <li>Show all columns and restore the default column widths</li>}
                        <li>Clear the sort order</li>
                        <li>Clear any column filters{this.props.hasSearch && " and search text"}</li>
                        {this.props.advancedFilterMode === "saved" && !accentUtils.isEmpty(this.props.selectedAdvancedFilterName) && (
                            <li>Clear the selected Advanced Filter "<b>{this.props.selectedAdvancedFilterName}</b>", including any changes made to it (saved Advanced Filters are not deleted)</li>
                        )}
                        {this.props.advancedFilterMode === "saved" && accentUtils.isEmpty(this.props.selectedAdvancedFilterName) && this.props.hasUnsavedAdvancedFilter && (
                            <li>Discard the unsaved Advanced Filter</li>
                        )}
                        {this.props.advancedFilterMode === "builder" && <li>Clear any filter conditions set via the Filter button</li>}
                        {this.hasQuickFilters && <li>Deselect the Quick Filter buttons (they stay on the grid unless removed below)</li>}
                        {this.props.hasDetails && <li>Hide row details</li>}
                        {this.props.paged && <li>Return to the first page</li>}
                    </ul>
                    {this.hasQuickFilters && (
                        <AccentCheckUnbound
                            tagName="resetGridLayoutRemoveQuickFilters"
                            label="Also remove Quick Filter buttons from this grid"
                            defaultValue={removeQuickFilters}
                            onChange={this.onToggleRemove}
                        />
                    )}
                    {this.hasQuickFilters && removeQuickFilters && (
                        <Alert color="warning" className="mt-2 mb-0">
                            <div>These Quick Filter buttons will be removed from this grid:</div>
                            {accentUtils.isNull(filterNames)
                                ? <AccentSpinner />
                                : <ul className="mb-0 mt-1">{filterNames.map((n, i) => <li key={`${i}_${n}`}>{n}</li>)}</ul>}
                            <div className="mt-2"><i>The saved filters are not deleted and can be added again later from the Quick Filter menu.</i></div>
                        </Alert>
                    )}
                </div>
            </AccentDialog>
        );
    }
}
